import {
  CopyPlus,
  Edit2,
  Logs,
  Repeat2,
  StickyNote,
  Trash2,
} from "lucide-react";
import SimpleListItem from "@/components/foundations/lists/SimpleListItem/SimpleListItem";
import type { TaskVisibleFields } from "@shared/taskVisibleFields";
import {
  repeatSummary,
  type RepeatRule,
} from "@/components/tasks/repeat/RepeatOptions/repeat-options-types";
import OptionsWrapper from "../OptionsWrapper/OptionsWrapper";

type Colors = { bar: string; soft: string; ink: string };
type Props = {
  title: string;
  details: string;
  listName: string;
  colors: Colors;
  visibleFields: TaskVisibleFields;
  repeatRule: RepeatRule;
  onToggleVisibility: (field: keyof TaskVisibleFields) => void;
  onOpenDisplay: () => void;
  onClone: () => void;
  onDelete: () => void;
  onBack: () => void;
};

function SectionLabel({
  children,
  color,
}: {
  children: string;
  color: string;
}) {
  return (
    <div
      className="ui-options-section__label px-1 text-[11px] font-semibold uppercase tracking-wider"
      style={{ color, opacity: 0.55 }}
    >
      {children}
    </div>
  );
}

export default function PatternOptionsView({
  title,
  details,
  listName,
  colors,
  visibleFields,
  repeatRule,
  onToggleVisibility,
  onOpenDisplay,
  onClone,
  onDelete,
  onBack,
}: Props) {
  return (
    <OptionsWrapper onBack={onBack} accentColor={colors.ink}>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Display</SectionLabel>
        <button
          type="button"
          onClick={onOpenDisplay}
          className="ui-options-display-item w-full overflow-hidden rounded-xl border text-left transition-opacity hover:opacity-80 active:opacity-60"
          style={{
            background: colors.soft,
            borderColor: `color-mix(in oklch, ${colors.bar} 28%, transparent)`,
          }}
        >
          <div className="flex items-stretch">
            <span
              className="w-1.5 shrink-0"
              style={{ background: colors.bar }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <span
                  className="truncate text-base font-bold leading-snug"
                  style={{ color: colors.ink }}
                >
                  {title || "New pattern"}
                </span>
                <span
                  className="max-w-32 shrink-0 truncate rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.14em]"
                  style={{
                    background: `color-mix(in oklch, ${colors.bar} 18%, transparent)`,
                    color: colors.ink,
                  }}
                >
                  {listName}
                </span>
              </div>
              {details && (
                <div
                  className="mt-0.5 truncate text-[15px] font-semibold"
                  style={{ color: colors.ink, opacity: 0.75 }}
                >
                  {details}
                </div>
              )}
            </div>
          </div>
        </button>
        <div
          className="px-1 pb-1 text-xs"
          style={{ color: colors.ink, opacity: 0.7 }}
        >
          Using list colors
        </div>
      </section>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Visibility</SectionLabel>
        <p className="px-1 pb-1 text-xs text-muted-foreground">
          Selected sections will be available when this pattern creates a task.
        </p>
        <div className="space-y-2">
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<Repeat2 className="h-4 w-4" />}
              title={`Repeat: ${repeatSummary(repeatRule)}`}
              leftIndicator={visibleFields.repeat ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={() => onToggleVisibility("repeat")}
              onLeftIndicatorClick={() => onToggleVisibility("repeat")}
            />
          </div>
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<StickyNote className="h-4 w-4" />}
              title="Edit note"
              leftIndicator={visibleFields.notes ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={() => onToggleVisibility("notes")}
              onLeftIndicatorClick={() => onToggleVisibility("notes")}
            />
          </div>
          <div className="overflow-hidden rounded-xl border">
            <SimpleListItem
              icon={<Logs className="h-4 w-4" />}
              title="Event log"
              leftIndicator={visibleFields.log ? "checked" : "empty"}
              rightIndicator="chevron"
              onClick={() => onToggleVisibility("log")}
              onLeftIndicatorClick={() => onToggleVisibility("log")}
            />
          </div>
        </div>
      </section>
      <section className="ui-options-section space-y-2">
        <SectionLabel color={colors.ink}>Actions</SectionLabel>
        <button
          type="button"
          onClick={onClone}
          className="ui-options-action flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors hover:bg-accent/20"
        >
          <CopyPlus
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <span>Clone pattern</span>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="ui-options-action flex w-full items-center gap-3 rounded-xl border border-destructive/30 px-4 py-3 text-sm text-destructive transition-colors hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          <span>Delete</span>
        </button>
      </section>
    </OptionsWrapper>
  );
}
